/**
 * Универсальный компонент списка посадок
 * Роль: Orchestrator - управляет режимами отображения и делегирует рендеринг
 */

import React from 'react';
import { Empty } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import type { Planting } from '../../models/Planting';
import PlantingsTable from './components/PlantingsTable';
import PlantingsGallery from './components/PlantingsGallery';
import { usePlantingsGrouping } from './hooks/usePlantingsGrouping';

export type PlantingsViewMode = 'table' | 'gallery';

interface PlantingsListProps {
  plantings: Planting[];
  loading?: boolean;
  viewMode?: PlantingsViewMode;
  farmId?: string | number;
  onEdit?: (planting: Planting) => void;
  onDelete?: (planting: Planting) => void;
  onView?: (planting: Planting) => void;
  rowSelection?: any;
  groupByCrop?: boolean;
  showLocationCode?: boolean;
}

const PlantingsList: React.FC<PlantingsListProps> = ({
  plantings,
  loading = false,
  viewMode = 'table',
  farmId,
  onEdit,
  onDelete,
  onView,
  rowSelection,
  groupByCrop = true,
  showLocationCode,
}) => {
  const navigate = useNavigate();
  const { saveId } = useParams<{ saveId: string }>();
  const groupedPlantings = usePlantingsGrouping(plantings);

  const handleView = (planting: Planting) => {
    if (onView) {
      onView(planting);
    } else {
      const plantingId = planting.uuid ?? planting.id;
      if (saveId) navigate(`/saves/${saveId}/plantings/${plantingId}`);
    }
  };

  if (plantings.length === 0 && !loading) {
    return <Empty description="Посадки не найдены" />;
  }

  if (viewMode === 'table') {
    return (
      <PlantingsTable
        plantings={plantings}
        loading={loading}
        rowSelection={rowSelection}
        onView={handleView}
        groupedData={groupByCrop ? groupedPlantings : undefined}
      />
    );
  }

  return (
    <PlantingsGallery
      plantings={plantings}
      onView={handleView}
      onEdit={onEdit}
      onDelete={onDelete}
      groupedData={groupByCrop ? groupedPlantings : undefined}
      showLocationCode={showLocationCode}
    />
  );
};

export default PlantingsList;
